import { IConfigBackupService } from "./IConfigBackupService";
import { Api } from "../../Api";
import { DataFrame, Method, Status } from "../../DataFrame";
import { ExalusConnectionService } from "../ExalusConnectionService";
import { IExalusConnectionService } from "../IExalusConnectionService";
import {
  ConfigBackupCreateResult,
  ConfigBackupRestoreResult,
  ControllerBackupConfigurationEntry,
  CreateBackupResponse,
} from "./IConfigBackupService";
import { ResponseResult } from "../FieldChangeResult";

export class ConfigBackupService implements IConfigBackupService {
  private static readonly FeatureNotSupportedMessage: string = "Config backup feature not supported in controller.";
  public static readonly ServiceName: string = "ConfigBackupService";
  private readonly _connection: IExalusConnectionService = Api.Get<IExalusConnectionService>(ExalusConnectionService.ServiceName);

    GetServiceName(): string {
    return ConfigBackupService.ServiceName;
    }

  async IsSupported(): Promise<boolean> {
    const response = await this._connection.SendAndWaitForResponseAsync<ControllerBackupConfigurationEntry[]>(new GetBackupsRequest(), 10_000, false);
    return response?.Status !== Status.ResourceDoesNotExists;
  }

  async GetBackupsAsync(): Promise<ControllerBackupConfigurationEntry[] | Status> {
    const response = await this._connection.SendAndWaitForResponseAsync<ControllerBackupConfigurationEntry[]>(new GetBackupsRequest(), 10_000, false);

    if (response?.Status === Status.ResourceDoesNotExists) {
      return Status.ResourceIsNotAvailable;
    }

    if (response?.Status !== Status.OK) {
      return (response?.Status as Status) ?? Status.UnknownError;
    }

    return response.Data ?? [];
  }

  async CreateBackupAsync(): Promise<CreateBackupResponse | ResponseResult<ConfigBackupCreateResult>> {
    const response = await this._connection.SendAndWaitForResponseAsync<unknown>(new CreateBackupRequest(), 15_000, false);

    if (response?.Status === Status.OK) {
      const data = response.Data as CreateBackupResponse | undefined;
      const configurationId = data?.ConfigurationId ?? "";
      return {
        ConfigurationId: configurationId,
      };
    }

    if (response?.Status === Status.ResourceDoesNotExists) {
      return new ResponseResult(ConfigBackupCreateResult.ResourceIsNotAvailable, ConfigBackupService.FeatureNotSupportedMessage);
    }

    if (response?.Status === Status.ResourceIsNotAvailable) {
      return new ResponseResult(ConfigBackupCreateResult.ResourceIsNotAvailable, this.GetResponseDataAsString(response.Data));
    }

    if (this.GetResponseDataAsString(response?.Data) === "BackupCreationFailed") {
      return new ResponseResult(ConfigBackupCreateResult.BackupCreationFailed, this.GetResponseDataAsString(response?.Data));
    }

    return new ResponseResult(ConfigBackupCreateResult.UnknownError, this.GetResponseDataAsString(response?.Data));
  }

  async RestoreBackupAsync(configurationId: string): Promise<ResponseResult<ConfigBackupRestoreResult>> {
    const response = await this._connection.SendAndWaitForResponseAsync<string>(new RestoreBackupRequest(configurationId), 40_000, false);
    return this.MapRestoreResponse(response?.Status, response?.Data as string | undefined);
  }

  async RestoreLatestBackupAsync(): Promise<ResponseResult<ConfigBackupRestoreResult>> {
    const response = await this._connection.SendAndWaitForResponseAsync<string>(new RestoreLatestBackupRequest(), 40_000, false);
    return this.MapRestoreResponse(response?.Status, response?.Data as string | undefined);
  }

  private MapRestoreResponse(status: Status | undefined, data: string | undefined): ResponseResult<ConfigBackupRestoreResult> {
    if (status === Status.OK) {
      return new ResponseResult(ConfigBackupRestoreResult.Restored, "");
    }

    if (status === Status.ResourceDoesNotExists) {
      return new ResponseResult(ConfigBackupRestoreResult.ResourceIsNotAvailable, ConfigBackupService.FeatureNotSupportedMessage);
    }

    if (data === "BackupNotFound") {
      return new ResponseResult(ConfigBackupRestoreResult.BackupNotFound, data ?? "");
    }

    if (status === Status.WrongData || data === "WrongBackupData") {
      return new ResponseResult(ConfigBackupRestoreResult.WrongBackupData, data ?? "");
    }

    if (status === Status.ResourceIsNotAvailable) {
      return new ResponseResult(ConfigBackupRestoreResult.ResourceIsNotAvailable, data ?? "");
    }

    if (data === "RestoreBackupFailed" || data === "RestoreLatestBackupFailed") {
      return new ResponseResult(ConfigBackupRestoreResult.RestoreFailed, data);
    }

    return new ResponseResult(ConfigBackupRestoreResult.UnknownError, data ?? "");
  }

  private GetResponseDataAsString(data: unknown): string {
    return typeof data === "string" ? data : "";
  }
}

class GetBackupsRequest extends DataFrame<any> {
  constructor() {
    super();
    this.Resource = "/configuration/cloud-backup/list";
    this.Method = Method.Get;
  }
}

class CreateBackupRequest extends DataFrame<any> {
  constructor() {
    super();
    this.Resource = "/configuration/cloud-backup/create";
    this.Method = Method.Post;
  }
}

class RestoreBackupRequest extends DataFrame<any> {
  constructor(configurationId: string) {
    super();
    this.Resource = "/configuration/cloud-backup/restore";
    this.Method = Method.Put;
    this.Data = {
      ConfigurationId: configurationId,
    };
  }
}

class RestoreLatestBackupRequest extends DataFrame<any> {
  constructor() {
    super();
    this.Resource = "/configuration/cloud-backup/restore/latest";
    this.Method = Method.Put;
  }
}